import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import PasswordInput from '../components/PasswordInput';
import {
  Box,
  Heading,
  Button,
  FormControl,
  FormLabel,
  Input,
  Select,
  InputGroup,
  InputRightElement,
  IconButton,
  VStack,
  HStack,
  useToast,
  Spinner,
  Alert,
  AlertIcon,
  Avatar,
  Flex,
  Text,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  Switch,
  Badge,
  Checkbox,
  CheckboxGroup,
  Stack,
  Grid,
} from '@chakra-ui/react';
import { LockIcon, ViewIcon, ViewOffIcon } from '@chakra-ui/icons';
import { apiService } from '../services/apiService';
import { getPhotoUrl } from '../config/image';

export default function MemberEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState(null);
  const [photoFile, setPhotoFile] = useState(null);
  const [nbCotisations, setNbCotisations] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [groups, setGroups] = useState([]);
  const [functions, setFunctions] = useState([]);
  
  // États pour le changement de mot de passe
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();
  
  const [formData, setFormData] = useState({
    matricule: '',
    numero_carte: '',
    nom: '',
    prenom: '',
    email: '',
    telephone: '',
    adresse: '',
    ville: '',
    code_postal: '',
    date_adhesion: '',
    date_naissance: '',
    lieu_naissance: '',
    baptise: false,
    date_bapteme: '',
    eglise_bapteme: '',
    confirme: false,
    date_confirmation: '',
    eglise_confirmation: '',
    type_membre: '',
    group_ids: [],
    fonction_id: '',
    is_active: true
  });

  useEffect(() => {
    const fetchMember = async () => {
      try {
        setLoading(true);
        const response = await apiService.makeRequest(`/members/${id}`);
        
        const formattedData = {
          ...response,
          date_adhesion: response.date_adhesion ? response.date_adhesion.split('T')[0] : '',
          date_naissance: response.date_naissance ? response.date_naissance.split('T')[0] : '',
          lieu_naissance: response.lieu_naissance || '',
          baptise: response.baptise === 1 || response.baptise === true,
          date_bapteme: response.date_bapteme ? response.date_bapteme.split('T')[0] : '',
          eglise_bapteme: response.eglise_bapteme || '',
          confirme: response.confirme === 1 || response.confirme === true,
          date_confirmation: response.date_confirmation ? response.date_confirmation.split('T')[0] : '',
          eglise_confirmation: response.eglise_confirmation || '',
          type_membre: response.type_membre || '',
          group_ids: response.group_ids ? response.group_ids.split(',').map(Number) : [],
          fonction_id: response.fonction_id || '',
          is_active: response.is_active === 1 || response.is_active === true
        };
        
        setFormData(formattedData);
      } catch (err) {
        setError(err.message || 'Erreur lors du chargement du membre');
        toast({
          title: 'Erreur',
          description: err.message,
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      } finally {
        setLoading(false);
      }
    };

    const fetchMetadata = async () => {
      try {
        const response = await apiService.makeRequest('/members/meta/groups-and-functions');
        console.log('=== METADATA LOADED ===');
        console.log('Groups:', response.groups);
        console.log('Functions:', response.functions);
        setGroups(response.groups || []);
        setFunctions(response.functions || []);
      } catch (err) {
        console.error('Error fetching metadata:', err);
      }
    };

    const fetchCotisations = async () => {
      try {
        const r = await apiService.makeRequest(`/contributions?user_id=${id}&limit=1`);
        setNbCotisations(r.pagination?.total ?? 0);
      } catch { setNbCotisations(0); }
    };

    fetchMember();
    fetchMetadata();
    fetchCotisations();
  }, [id, toast]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    let processedValue = value;
    
    if (name === 'nom' || name === 'prenom') {
      processedValue = value.replace(/[^a-zA-ZÀ-ÿ\s\-']/g, '');
    }
    
    if (name === 'telephone') {
      processedValue = value.replace(/[^\d+\s]/g, '');
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: processedValue
    }));
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        toast({
          title: 'Erreur',
          description: 'Veuillez sélectionner une image valide',
          status: 'error',
          duration: 3000,
        });
        return;
      }

      if (file.size > 5 * 1024 * 1024) {
        toast({
          title: 'Erreur',
          description: "L'image ne doit pas dépasser 5MB",
          status: 'error',
          duration: 3000,
        });
        return;
      }

      setPhotoFile(file);
      
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Fonction pour changer le mot de passe
  const handleDelete = async () => {
    setDeleting(true);
    try {
      await apiService.makeRequest(`/members/${id}`, { method: 'DELETE' });
      toast({ title: 'Membre supprimé', status: 'success', duration: 3000 });
      navigate('/members');
    } catch (e) {
      toast({ title: 'Erreur', description: e.message, status: 'error', duration: 4000 });
    } finally {
      setDeleting(false);
      onDeleteClose();
    }
  };

  const handlePasswordChange = async () => {
    if (newPassword !== confirmPassword) {
      toast({
        title: 'Erreur',
        description: 'Les mots de passe ne correspondent pas',
        status: 'error',
        duration: 3000,
      });
      return;
    }

    if (newPassword.length < 6) {
      toast({
        title: 'Erreur',
        description: 'Le mot de passe doit contenir au moins 6 caractères',
        status: 'error',
        duration: 3000,
      });
      return;
    }

    setIsChangingPassword(true);

    try {
      const url = `${process.env.REACT_APP_API_URL}/auth/admin/users/${id}/password`;
      console.log('URL appelée:', url); // DEBUG
      console.log('User ID:', id); // DEBUG
      const response = await fetch(`${process.env.REACT_APP_API_URL}/auth/admin/users/${id}/password`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
        },
        body: JSON.stringify({ newPassword })
      });
      console.log('Statut de la réponse:', response.status); // DEBUG
    
      const data = await response.json();
      console.log('Réponse du serveur:', data); // DEBUG

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Erreur lors du changement de mot de passe');
      }

      toast({
        title: 'Succès',
        description: `Le mot de passe de ${formData.prenom} ${formData.nom} a été modifié. L'utilisateur peut maintenant se connecter avec le nouveau mot de passe.`,
        status: 'success',
        duration: 5000,
      });
      setNewPassword('');
      setConfirmPassword('');
      onClose();

    } catch (error) {
      console.error('Erreur complète:', error); // DEBUG
      toast({
        title: 'Erreur',
        description: error.message,
        status: 'error',
        duration: 3000,
      });
    } finally {
      setIsChangingPassword(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setSaving(true);
      
      const formatPhoneNumber = (phone) => {
        if (!phone) return '';
        let cleaned = phone.replace(/[^\d+]/g, '');
        if (cleaned.startsWith('0')) {
          cleaned = '+33' + cleaned.substring(1);
        }
        if (!cleaned.startsWith('+')) {
          cleaned = '+33' + cleaned;
        }
        return cleaned;
      };
      
      const cleanedData = Object.entries(formData).reduce((acc, [key, value]) => {
        const allowedFields = [
          'matricule', 'nom', 'prenom', 'email', 'telephone',
          'date_naissance', 'lieu_naissance', 'adresse', 'adresse_postale',
          'code_postal', 'ville', 'date_adhesion', 'numero_carte',
          'baptise', 'date_bapteme', 'eglise_bapteme',
          'confirme', 'date_confirmation', 'eglise_confirmation',
          'type_membre', 'group_ids', 'fonction_id', 'is_active', 'recevoir_lectures_whatsapp'
        ];

        if (allowedFields.includes(key) && value !== '' && value !== null && value !== undefined) {
          if (key === 'telephone') {
            acc[key] = formatPhoneNumber(value);
          } else {
            acc[key] = value;
          }
        }
        return acc;
      }, {});
      
      if (photoFile) {
        cleanedData.photo = photoFile;
      }
      
      await apiService.updateMember(id, cleanedData);

      const updatedMember = await apiService.makeRequest(`/members/${id}`);
      const refreshedData = {
        ...updatedMember,
        date_adhesion: updatedMember.date_adhesion ? updatedMember.date_adhesion.split('T')[0] : '',
        date_naissance: updatedMember.date_naissance ? updatedMember.date_naissance.split('T')[0] : '',
        lieu_naissance: updatedMember.lieu_naissance || '',
        baptise: updatedMember.baptise === 1 || updatedMember.baptise === true,
        date_bapteme: updatedMember.date_bapteme ? updatedMember.date_bapteme.split('T')[0] : '',
        eglise_bapteme: updatedMember.eglise_bapteme || '',
        confirme: updatedMember.confirme === 1 || updatedMember.confirme === true,
        date_confirmation: updatedMember.date_confirmation ? updatedMember.date_confirmation.split('T')[0] : '',
        eglise_confirmation: updatedMember.eglise_confirmation || '',
        group_ids: updatedMember.group_ids
          ? String(updatedMember.group_ids).split(',').map(Number).filter(Boolean)
          : [],
      };
      setFormData(refreshedData);
      setPhotoFile(null);
      setPhotoPreview(null);

      toast({
        title: 'Succès',
        description: 'Membre modifié avec succès',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      
    } catch (err) {
      let errorMessage = err.message || 'Erreur lors de la modification';
      
      if (err.errors && Array.isArray(err.errors)) {
        errorMessage = err.errors.map(e => `${e.field}: ${e.message}`).join('\n');
      } else if (err.data && err.data.errors && Array.isArray(err.data.errors)) {
        errorMessage = err.data.errors.map(e => `${e.field}: ${e.message}`).join('\n');
      }
      
      toast({
        title: 'Erreur de validation',
        description: errorMessage,
        status: 'error',
        duration: 8000,
        isClosable: true,
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minH="400px">
        <Spinner size="xl" color="blue.500" />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert status="error" borderRadius="md">
        <AlertIcon />
        {error}
      </Alert>
    );
  }

  return (
    <Box maxW="800px" mx="auto">
      <Flex justify="space-between" align="center" mb={6}>
        <Heading size="lg">Modifier le membre</Heading>
        <Button variant="ghost" onClick={() => navigate('/members')}>
          Retour
        </Button>
      </Flex>

      <Box bg="white" p={6} borderRadius="md" shadow="sm">
        <Flex direction="column" align="center" mb={6}>
          <Avatar
            size="2xl"
            name={`${formData.prenom} ${formData.nom}`}
            src={photoPreview || (formData.photo ? getPhotoUrl(formData.photo, Date.now()) : undefined)}
            mb={4}
          />          
          <Input
            type="file"
            accept="image/*"
            onChange={handlePhotoChange}
            display="none"
            id="photo-upload"
          />
          <Button
            as="label"
            htmlFor="photo-upload"
            size="sm"
            colorScheme="blue"
            variant="outline"
            cursor="pointer"
          >
            Changer la photo
          </Button>
          {photoFile && (
            <Text fontSize="sm" color="gray.600" mt={2}>
              Nouvelle photo sélectionnée: {photoFile.name}
            </Text>
          )}
        </Flex>

        <form onSubmit={handleSubmit}>
          <VStack spacing={4} align="stretch">
            <FormControl isRequired>
              <FormLabel>Matricule</FormLabel>
              <Input
                name="matricule"
                value={formData.matricule}
                onChange={handleChange}
                placeholder="Matricule"
              />
            </FormControl>

            <FormControl>
              <FormLabel>Numéro de carte</FormLabel>
              <Input
                name="numero_carte"
                value={formData.numero_carte || ''}
                onChange={handleChange}
                placeholder="CARD-000001"
              />
            </FormControl>

            <HStack spacing={4}>
              <FormControl isRequired>
                <FormLabel>Nom</FormLabel>
                <Input
                  name="nom"
                  value={formData.nom}
                  onChange={handleChange}
                  placeholder="Nom"
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Prénom</FormLabel>
                <Input
                  name="prenom"
                  value={formData.prenom}
                  onChange={handleChange}
                  placeholder="Prénom"
                />
              </FormControl>
            </HStack>

            <FormControl>
              <FormLabel>Email</FormLabel>
              <Input
                type="email"
                name="email"
                value={formData.email || ''}
                onChange={handleChange}
                placeholder="email@example.com"
              />
            </FormControl>

            <FormControl>
              <FormLabel>Téléphone</FormLabel>
              <Input
                name="telephone"
                value={formData.telephone || ''}
                onChange={handleChange}
                placeholder="06 12 34 56 78 ou +33 6 12 34 56 78"
              />
            </FormControl>

            <FormControl>
              <FormLabel>Adresse</FormLabel>
              <Input
                name="adresse"
                value={formData.adresse || ''}
                onChange={handleChange}
                placeholder="Adresse"
              />
            </FormControl>

            <HStack spacing={4}>
              <FormControl>
                <FormLabel>Code postal</FormLabel>
                <Input
                  name="code_postal"
                  value={formData.code_postal || ''}
                  onChange={handleChange}
                  placeholder="75000"
                />
              </FormControl>

              <FormControl>
                <FormLabel>Ville</FormLabel>
                <Input
                  name="ville"
                  value={formData.ville || ''}
                  onChange={handleChange}
                  placeholder="Paris"
                />
              </FormControl>
            </HStack>

            <HStack spacing={4}>
              <FormControl>
                <FormLabel>Date de naissance</FormLabel>
                <Input
                  type="date"
                  name="date_naissance"
                  value={formData.date_naissance || ''}
                  onChange={handleChange}
                />
              </FormControl>

              <FormControl>
                <FormLabel>Lieu de naissance</FormLabel>
                <Input
                  name="lieu_naissance"
                  value={formData.lieu_naissance || ''}
                  onChange={handleChange}
                  placeholder="Ville de naissance"
                />
              </FormControl>
            </HStack>

            <FormControl>
              <FormLabel>Date d'adhésion</FormLabel>
              <Input
                type="date"
                name="date_adhesion"
                value={formData.date_adhesion || ''}
                onChange={handleChange}
              />
            </FormControl>

            {/* Informations religieuses */}
            <Text fontWeight="600" fontSize="sm" color="gray.600">Informations religieuses</Text>

            <FormControl display="flex" alignItems="center" w="auto">
              <FormLabel mb={0} minW="80px">Baptisé(e)</FormLabel>
              <Switch
                isChecked={formData.baptise}
                onChange={(e) => setFormData(prev => ({ ...prev, baptise: e.target.checked }))}
                colorScheme="green"
              />
            </FormControl>

            {formData.baptise && (
              <Grid templateColumns={{ base: '1fr', sm: '1fr 1fr' }} gap={3}>
                <FormControl>
                  <FormLabel fontSize="sm">Date du baptême</FormLabel>
                  <Input
                    type="date"
                    name="date_bapteme"
                    value={formData.date_bapteme || ''}
                    onChange={handleChange}
                    size="sm"
                  />
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="sm">Église du baptême</FormLabel>
                  <Input
                    name="eglise_bapteme"
                    value={formData.eglise_bapteme || ''}
                    onChange={handleChange}
                    placeholder="Nom de l'église"
                    size="sm"
                  />
                </FormControl>
              </Grid>
            )}

            <FormControl display="flex" alignItems="center" w="auto">
              <FormLabel mb={0} minW="80px">Confirmé(e)</FormLabel>
              <Switch
                isChecked={formData.confirme}
                onChange={(e) => setFormData(prev => ({ ...prev, confirme: e.target.checked }))}
                colorScheme="green"
              />
            </FormControl>

            {formData.confirme && (
              <Grid templateColumns={{ base: '1fr', sm: '1fr 1fr' }} gap={3}>
                <FormControl>
                  <FormLabel fontSize="sm">Date de confirmation</FormLabel>
                  <Input
                    type="date"
                    name="date_confirmation"
                    value={formData.date_confirmation || ''}
                    onChange={handleChange}
                    size="sm"
                  />
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="sm">Église de confirmation</FormLabel>
                  <Input
                    name="eglise_confirmation"
                    value={formData.eglise_confirmation || ''}
                    onChange={handleChange}
                    placeholder="Nom de l'église"
                    size="sm"
                  />
                </FormControl>
              </Grid>
            )}

            {/* Informations administratives */}
            {console.log('=== RENDERING ADMIN FIELDS ===', { groups: groups.length, functions: functions.length, type_membre: formData.type_membre, groupe_id: formData.groupe_id })}
            <HStack spacing={4}>
              <FormControl>
                <FormLabel>Type de membre</FormLabel>
                <Select
                  name="type_membre"
                  value={formData.type_membre || ''}
                  onChange={handleChange}
                  placeholder="Sélectionner un type"
                >
                  <option value="Communiant">Communiant</option>
                  <option value="Non communiant">Non communiant</option>
                </Select>
              </FormControl>

              <FormControl>
                <FormLabel>Groupe(s)</FormLabel>
                <CheckboxGroup
                  value={formData.group_ids.map(String)}
                  onChange={(vals) => setFormData(prev => ({ ...prev, group_ids: vals.map(Number) }))}
                >
                  <Stack spacing={2} mt={1}>
                    {groups.map((group) => (
                      <Checkbox key={group.id} value={String(group.id)}>
                        {group.nom}
                      </Checkbox>
                    ))}
                  </Stack>
                </CheckboxGroup>
              </FormControl>
            </HStack>

            <FormControl>
              <FormLabel>Fonction</FormLabel>
              <Select
                name="fonction_id"
                value={formData.fonction_id || ''}
                onChange={handleChange}
                placeholder="Sélectionner une fonction"
              >
                {functions.map((func) => (
                  <option key={func.id} value={func.id}>
                    {func.nom}
                  </option>
                ))}
              </Select>
            </FormControl>

            {/* Statut du membre */}
            <Box mt={2} p={4} borderRadius="md" border="1px" borderColor={formData.is_active ? 'green.200' : 'red.200'} bg={formData.is_active ? 'green.50' : 'red.50'}>
              <FormControl display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <FormLabel mb={0} fontWeight="semibold">Statut du compte</FormLabel>
                  <Text fontSize="sm" color="gray.500">
                    Un membre inactif ne peut plus se connecter à l'application
                  </Text>
                </Box>
                <HStack spacing={3}>
                  <Badge colorScheme={formData.is_active ? 'green' : 'red'} fontSize="sm" px={2} py={1}>
                    {formData.is_active ? 'Actif' : 'Inactif'}
                  </Badge>
                  <Switch
                    isChecked={formData.is_active}
                    onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.checked }))}
                    colorScheme="green"
                    size="lg"
                  />
                </HStack>
              </FormControl>
            </Box>

            {/* WhatsApp lectures */}
            <Box mt={2} p={4} borderRadius="md" border="1px" borderColor="green.200" bg="green.50">
              <FormControl display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <FormLabel mb={0} fontWeight="semibold">📱 Lectures bibliques WhatsApp</FormLabel>
                  <Text fontSize="sm" color="gray.500">
                    Recevoir la lecture du jour par WhatsApp
                  </Text>
                </Box>
                <Switch
                  isChecked={!!formData.recevoir_lectures_whatsapp}
                  onChange={(e) => setFormData(prev => ({ ...prev, recevoir_lectures_whatsapp: e.target.checked }))}
                  colorScheme="green"
                  size="lg"
                />
              </FormControl>
            </Box>

            {/* Section Sécurité */}
            <Box mt={6} pt={6} borderTop="1px" borderColor="gray.200">
              <Heading size="md" mb={4}>Sécurité</Heading>
              <HStack spacing={3}>
                <Button
                  colorScheme="orange"
                  leftIcon={<LockIcon />}
                  onClick={onOpen}
                  size="sm"
                >
                  Modifier le mot de passe
                </Button>
                {nbCotisations === 0 && (
                  <Button
                    colorScheme="red"
                    variant="outline"
                    size="sm"
                    onClick={onDeleteOpen}
                  >
                    Supprimer le membre
                  </Button>
                )}
              </HStack>
            </Box>

            <HStack spacing={4} pt={4}>
              <Button
                type="submit"
                colorScheme="blue"
                isLoading={saving}
                loadingText="Enregistrement..."
                flex={1}
              >
                Enregistrer les modifications
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate('/members')}
                isDisabled={saving}
              >
                Annuler
              </Button>
            </HStack>
          </VStack>
        </form>
      </Box>

      {/* Modal de confirmation de suppression */}
      <Modal isOpen={isDeleteOpen} onClose={onDeleteClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader color="red.600">Supprimer le membre</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Alert status="error" borderRadius="md" mb={3}>
              <AlertIcon />
              <Box fontSize="sm">
                Vous êtes sur le point de supprimer <strong>{formData.prenom} {formData.nom}</strong>.
                Le compte sera désactivé (les données sont conservées en base).
              </Box>
            </Alert>
            <Text fontSize="sm" color="gray.600">Cette action est réservée aux membres sans cotisation enregistrée.</Text>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onDeleteClose}>Annuler</Button>
            <Button colorScheme="red" onClick={handleDelete} isLoading={deleting} loadingText="Suppression...">
              Confirmer la suppression
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Modal de changement de mot de passe */}
      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Modifier le mot de passe</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <Alert status="warning" borderRadius="md">
                <AlertIcon />
                <Box fontSize="sm">
                  Vous êtes sur le point de modifier le mot de passe de <strong>{formData.prenom} {formData.nom}</strong>. 
                  Cette action est irréversible et l'utilisateur sera notifié par email.
                </Box>
              </Alert>

              <FormControl isRequired>
                <FormLabel>Nouveau mot de passe</FormLabel>
                <PasswordInput
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Minimum 6 caractères"
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Confirmer le mot de passe</FormLabel>
                <PasswordInput
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Retapez le mot de passe"
                />
              </FormControl>
            </VStack>
          </ModalBody>

          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>
              Annuler
            </Button>
            <Button
              colorScheme="orange"
              onClick={handlePasswordChange}
              isLoading={isChangingPassword}
              loadingText="Modification..."
            >
              Modifier le mot de passe
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}